import React, { useCallback, useEffect, useState, type MouseEvent, type ReactNode } from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from 'styled-components';
import { withAnalyticsEvents, type UIAnalyticsEvent } from '@atlaskit/analytics-next';
import InlineEdit from '@atlaskit/inline-edit';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import { JiraInlineDialog as InlineDialog } from '@atlassian/jira-inline-dialog/src/ui/jira-inline-dialog.tsx';
import { useIntl } from '@atlassian/jira-intl';
import type { MessageDescriptorV2 as MessageDescriptor } from '@atlassian/jira-intl/src/v2/types.tsx';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { fetchIssueTypeNames } from '../../services/fetch-issue-type-names/fetch-issue-type-names.tsx';
import type { ValidationError } from '../../types/validation.tsx';
import { validateIssueTypeName } from '../../utils/validation/issue-type/index.tsx';
import type { ValidationMessages } from '../../utils/validation/issue-type/types.tsx';

const CY_KEY = 'data-test-cypress';
const CY_ISSUE_TYPE_NAME = 'project-settings-apps.issue-type-page.main-header.name-inline-input';
const CY_ISSUE_TYPE_NAME_PROPS = { [CY_KEY]: CY_ISSUE_TYPE_NAME } as const;

export type Props = {
	/**
	 * Used to programmatically re-mount the InlineEdit component when changes are discarded in the UI. This is
	 * necessary to correctly reset the components internal edit state.
	 */
	issueTypeResetCount: number;
	issueTypeId: number;
	canBeEdited: boolean;
	projectId: number;
	onValidateIssueTypeName?: (
		issueTypeName: string,
		existingIssueTypeNames: string[],
		validationMessages: ValidationMessages,
	) => ValidationError[];
	validationMessages?: ValidationMessages;
	/**
	 * value is used as the defaultValue for InlineEdit. Whenever edit mode is requested for the component the
	 * input value is set to InlineEdit's defaultValue prop. If we were to use the current input value as the
	 * defaultValue then validation would never be run as no changes would be detected. Therefore this value MUST ONLY
	 * be updated only when changes are confirmed.
	 */
	value: string;
	/**
	 * List of existing names to check if value is duplicated.
	 * In case no value is provided, component will internally call fetchIssueTypeNames and check duplicate agains it.
	 * In case value is provided, it will be sent to issue type name validator, for further validations.
	 */
	existingNames?: string[];
	onChange: (arg1: string) => void;
	messages: {
		editNameButtonLabel: MessageDescriptor;
		// TODO: make required when cleaning up jsm_rtc_document_title_a11y
		editNameInputLabel?: MessageDescriptor;
	};
};

type IssueType = {
	readonly name: string;
	readonly id: number;
};
/**
 * This component is a bit bloated, let me explain why.
 * SWIFT page was built using AK inline-edit ^7.0.1. This version supported a stateless component which fits well into
 * our Redux model (field value, validation etc in the Redux store controlled via props).
 *
 * We've since been forced to upgrade to inline-edit 10.0.32 which has it's own internal state that handles modifying
 * and validating the field using AK form and exposes less control through the props API. Relevant changes documented
 * here https://atlaskit.atlassian.com/packages/design-system/inline-edit/docs/upgrade-guide
 *
 * This means we have to jump through a few hoops to keep behaviour semi-consistent in SWIFT, e.g. Overriding the change
 * event for the edit view to also update our redux store (so the 'Save changes' button can be enabled). Using the
 * validation popup that ships with inline-edit instead of the red message text we use elsewhere in SWIFT.
 *
 * The key challenge here is keeping the Redux and component state in sync with the limited API inline-edit provides.
 * Possible ways to improve this:
 *  - Update AK inline-edit to export a stateless component (seems unlikely as the new API intentionally steers away
 *  from this).
 *  - DIY inline-edit component to better fit our use case.
 *  - Make updating the field atomic from other actions in SWIFT (only need to update our Redux store when the change is
 *  confirmed).
 */
export const NameInlineInput = (props: Props) => {
	const {
		canBeEdited,
		onChange,
		value,
		issueTypeResetCount,
		issueTypeId,
		projectId,
		validationMessages = {},
		onValidateIssueTypeName = validateIssueTypeName,
		existingNames = null,
		messages,
	} = props;
	const { formatMessage } = useIntl();
	const [issueTypesPromise, setIssueTypesPromise] = useState<null | Promise<readonly IssueType[]>>(
		null,
	);
	const getAllIssueTypes = (): Promise<ReadonlyArray<IssueType>> => {
		if (!issueTypesPromise) {
			const newPromise = fetchIssueTypeNames(projectId).catch(() => {
				// we shouldn't keep failed promise
				setIssueTypesPromise(null);
				return [];
			});

			setIssueTypesPromise(newPromise);
			return newPromise;
		}

		return issueTypesPromise;
	};

	useEffect(() => {
		// we should re-load issue type names when user switches to another project
		setIssueTypesPromise(null);
	}, [projectId]);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const validateAndFormatMessage = async (newValue: any) => {
		let existingIssueTypeNames = existingNames;
		if (!existingIssueTypeNames) {
			const issueTypes = await getAllIssueTypes();
			existingIssueTypeNames = issueTypes
				.filter(({ id }) => id !== issueTypeId)
				.map(({ name }) => name);
		}

		const errors = onValidateIssueTypeName(newValue, existingIssueTypeNames, validationMessages);
		if (errors.length > 0) {
			return formatMessage(errors[0].messageDescriptor, errors[0].values);
		}
		return '';
	};

	const sendAnalytics = (event: MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
		fireUIAnalytics(analyticsEvent, 'textField clicked', 'issueLayoutTitle');
	};

	const renderReadView = useCallback(() => <ReadWrapper>{value}</ReadWrapper>, [value]);

	const renderEditView = useCallback(
		// @ts-expect-error - TS7031 - Binding element 'errorMessage' implicitly has an 'any' type.
		({ errorMessage, ...fieldProps }) => {
			const editNameInputLabelMessage = messages.editNameInputLabel;
			const editInputLabel = fg('jsm_rtc_document_title_a11y')
				? editNameInputLabelMessage && formatMessage(editNameInputLabelMessage)
				: undefined;

			return (
				<InlineDialog
					content={errorMessage}
					isOpen={fieldProps.isInvalid}
					placement="right"
					messageId="project-settings-apps-issue-type-page-product-features.common.ui.name-inline-input.inline-dialog"
					messageType="transactional"
				>
					<TextFieldWrapper>
						<Textfield {...fieldProps} aria-label={editInputLabel} autoFocus />
					</TextFieldWrapper>
				</InlineDialog>
			);
		},
		[messages.editNameInputLabel, formatMessage],
	);
	const editButtonLabel = value
		? `${value} - ${formatMessage(messages.editNameButtonLabel)}`
		: formatMessage(messages.editNameButtonLabel);

	return (
		<IssueTypeHeaderAlignmentWrapperWithAnalytics
			onClick={sendAnalytics}
			{...CY_ISSUE_TYPE_NAME_PROPS}
		>
			{canBeEdited ? (
				<InlineEdit
					key={issueTypeResetCount}
					readView={renderReadView}
					// @ts-expect-error - TS2322 - Type '({ errorMessage, ...fieldProps }: { [x: string]: any; errorMessage: any; }) => Element' is not assignable to type '(fieldProps: ExtendedFieldProps<string>, ref: RefObject<any>) => ReactNode'.
					editView={renderEditView}
					editButtonLabel={editButtonLabel}
					defaultValue={value}
					onConfirm={onChange}
					readViewFitContainerWidth
					validate={validateAndFormatMessage}
				/>
			) : (
				<NonEditableReadWrapper>{value}</NonEditableReadWrapper>
			)}
		</IssueTypeHeaderAlignmentWrapperWithAnalytics>
	);
};

// This is a magic number that is calculated based on:
// - 300px (default navigation sidebar width)
// - 360px - fields sidebar width
// - 592px min content width from the spec - https://hello.atlassian.net/wiki/spaces/JPLAT/pages/509111195/Field+Responsiveness
// using this media query is not perfect (because navigation sidebar can have custom width or they may be extra
// help sidebar on the right side), but it should work well in most cases and it also allows us to avoid
// using JS to check current width of content area
const getMaxWidth = () => 300 + 360 + 592;

// Override the default AK Textfield theme to ensure typography styles match the parent. We also make minor
// adjustments to the input spacing to match design specs. https://hello.atlassian.net/wiki/spaces/JPLAT/pages/190076898/Issue+types+spec+-+Edit+issue+type

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TextFieldWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'input[data-ds--text-field--input]': {
		font: token('font.heading.large'),
		margin: `${token('space.negative.025')} 0`,
	},
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IssueTypeHeaderAlignmentWrapper = styled.div<any>({
	/* Inline edit has a fixed margin top of 8px that we need to override */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	div: {
		marginTop: 0,
	},
});

// This wrapper is here to avoid content jumps upon triggering inline-edit
// TODO: migrate to object syntax. Autofix is available for many cases. Remove the eslint-disable for @atlaskit/design-system/no-styled-tagged-template-expression to check.
// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ReadWrapper = styled.h1<{
	children?: ReactNode;
}>`
	font: ${token('font.heading.large')};
	overflow-wrap: break-word;
	margin-top: 0;
	padding: ${token('space.075')};

	@media screen and (max-width: ${getMaxWidth()}px) {
		text-overflow: ellipsis;
		overflow: hidden;
		white-space: nowrap;
	}
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const NonEditableReadWrapper = styled(ReadWrapper)({
	/* Add padding to the non-editable wrapper matching the padding of the inline edit component to make sure they're aligned. */
	padding: token('space.100'),
});

const IssueTypeHeaderAlignmentWrapperWithAnalytics = withAnalyticsEvents({
	onClick: {
		action: 'clicked',
	},
})(IssueTypeHeaderAlignmentWrapper);
